import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { observer } from 'mobx-react-lite';
import { auth } from '../firebase/config';
import { getUserProfile } from '../user/user.service';
import { RootStoreContext } from '../core/root-store';
import LoginUI from './ui/login.ui';
import { LoginFormData } from './forms.types';
import { OnboardingStep } from '../onboarding/onboarding.type';
import { getFirebaseLoginErrorMessage } from './helpers';

const Login = observer(() => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const rootStore = useContext(RootStoreContext);
  const userStore = rootStore.userStore;
  const onboardingStore = rootStore.onboardingStore;
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userProfile = await getUserProfile(user.uid);
          if (userProfile) {
            // User is already logged in, redirect appropriately
            if (userProfile.webOnboarding?.onboardingStep === OnboardingStep.OnboardingCompleted) {
              navigate('/dashboard');
            } else {
              const nextStep = userProfile.webOnboarding?.onboardingStep
                ? onboardingStore.getNextStep(userProfile.webOnboarding.onboardingStep)
                : OnboardingStep.PhoneNumber;
              navigate(`/onboarding?step=${nextStep}`);
            }
          }
        } catch (error) {
          setCheckingAuth(false);
        }
      } else {
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, onboardingStore]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError('');

      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);

      // Wait longer for Firebase to establish and persist the session
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Manually fetch and update userStore
      await new Promise(resolve => setTimeout(resolve, 100));

      const userProfile = await getUserProfile(userCredential.user.uid);
      if (userProfile) {
        userStore.setReturningUser(true);
        userStore.update(userProfile);
        await new Promise(resolve => setTimeout(resolve, 100));

        // Check onboarding status and redirect
        if (userProfile.webOnboarding?.onboardingStep === OnboardingStep.OnboardingCompleted)
        {
          navigate('/dashboard');
        } else {
          const nextStep = userProfile.webOnboarding?.onboardingStep
            ? onboardingStore.getNextStep(userProfile.webOnboarding.onboardingStep)
            : OnboardingStep.PhoneNumber;

          navigate(`/onboarding?step=${nextStep}`);
        }
      } else {
        setError('Failed to load user profile. Please try again.');
      }

    } catch (error: any) {
      setError(getFirebaseLoginErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Don't render login form while checking auth
  if (checkingAuth) {
    return null;
  }

  return (
    <LoginUI
      formData={formData}
      error={error}
      loading={loading}
      onSubmit={handleSubmit}
      onChange={handleChange}
    />
  );
});

export default Login;
